import {
  IconBox,
  IconBuildingStore,
  IconChartArrowsVertical,
  IconChartBar,
  IconCreditCard,
  IconReceipt,
} from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Leaderboard,
  MasterStackBarChart,
  ScalrCarousel,
  TreeTable,
} from '@/components/customComponents'
import { createTab, TabConfig } from '@/components/TabSkeleton'
import { DateRangeValidator } from '@/components/ui/charts/DateRangeValidator'
import { MasterChart } from '@/components/ui/charts/MasterChart'
import { Tabs, TabsList } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

import { chartColors } from '@/constants/COLOR_PALETTE'
import { toast, usePnLMetrics, usePnLStackChartMetrics } from '@/hooks'
import usePnlStoreHealthCheck from '@/hooks/clarity/usePnlStoreHealthCheck'
import { TranslationFunctionType } from '@/i18n'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { dateToString } from '@/utils'

const MINI_METRIC_CONFIGS = (t: TranslationFunctionType) => [
  {
    key: 'storeProfits' as const,
    header: t('clarity.pnl.storeProfits'),
    icon: (
      <IconCreditCard className="w-6 h-6" style={{ color: chartColors[0] }} />
    ),
  },
  {
    key: 'netSales' as const,
    header: t('clarity.pnl.netSales'),
    icon: (
      <IconChartArrowsVertical
        className="w-6 h-6"
        style={{ color: chartColors[1] }}
      />
    ),
  },
  {
    key: 'cogs' as const,
    header: t('clarity.metrics.cogs'),
    icon: <IconBox className="w-6 h-6" style={{ color: chartColors[2] }} />,
  },
  {
    key: 'storeLabor' as const,
    header: t('clarity.pnl.storeLabor'),
    icon: (
      <IconChartBar className="w-6 h-6" style={{ color: chartColors[3] }} />
    ),
  },
  {
    key: 'opEx' as const,
    header: t('clarity.pnl.opEx'),
    icon: <IconReceipt className="w-6 h-6" style={{ color: chartColors[4] }} />,
  },
  {
    key: 'rentAndOccupancy' as const,
    header: t('clarity.pnl.rentAndOccupancy'),
    icon: (
      <IconBuildingStore
        className="w-6 h-6"
        style={{ color: chartColors[5] }}
      />
    ),
  },
]

const CHART_DATA_CONFIGS = (t: TranslationFunctionType) => [
  {
    key: 'cogs' as const,
    header: t('clarity.metrics.cogs'),
    icon: <IconBox className="w-6 h-6" style={{ color: chartColors[2] }} />,
  },
  {
    key: 'storeLabor' as const,
    header: t('clarity.pnl.storeLabor'),
    icon: (
      <IconChartBar className="w-6 h-6" style={{ color: chartColors[3] }} />
    ),
  },
  {
    key: 'opEx' as const,
    header: t('clarity.pnl.opEx'),
    icon: <IconReceipt className="w-6 h-6" style={{ color: chartColors[4] }} />,
  },
  {
    key: 'rentAndOccupancy' as const,
    header: t('clarity.pnl.rentAndOccupancy'),
    icon: (
      <IconBuildingStore
        className="w-6 h-6"
        style={{ color: chartColors[5] }}
      />
    ),
  },
]

export default function ProfitAndLossPage() {
  const { t } = useTranslation()
  const { selectedBranch, date, dateFilterType } =
    useReduxBaseFilter('profit-and-loss')
  const refreshTrigger = useAppSelector(
    selectPageRefreshTrigger('profit-and-loss')
  )

  const [executeStackChartMetrics, setExecuteStackChartMetrics] =
    useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setExecuteStackChartMetrics(true)
    }, 1500)

    return () => clearTimeout(timer)
  }, [])

  const {
    healthData,
    storeHealthCheckColumns,
    loading: storeHealthCheckLoading,
    error: storeHealthCheckError,
    refetch: storeHealthCheckRefetch,
  } = usePnlStoreHealthCheck(
    dateToString(date, 'from', 'yyyy-mm'),
    dateToString(date, 'to', 'yyyy-mm'),
    dateFilterType
  )

  const {
    hierarchyData,
    metricCardData,
    metricCardTrendData,
    pnlTableColumns,
    loading,
    error: pnlMetricsError,
    refetch: pnlRefetch,
  } = usePnLMetrics(
    dateToString(date, 'from', 'yyyy-mm'),
    dateToString(date, 'to', 'yyyy-mm'),
    selectedBranch
  )

  const {
    metricCardRecord,
    loading: stackChartLoading,
    error: stackChartError,
    refetch: stackChartRefetch,
  } = usePnLStackChartMetrics(
    executeStackChartMetrics ? selectedBranch : '',
    executeStackChartMetrics ? date : undefined
  )

  const getHierarchyDataByTopLevel = (id: string) => {
    return hierarchyData?.find((item) => item.id === id)?.children || []
  }

  const { tabs } = useMemo(() => {
    const configs: TabConfig[] = [
      {
        value: 'sales',
        title: t('clarity.pnl.sales'),
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_sales')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
          />
        ),
      },
      {
        value: 'cogs',
        title: t('clarity.metrics.cogs'),
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_cogs')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
          />
        ),
      },
      {
        value: 'storeLabor',
        title: t('clarity.pnl.storeLabor'),
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_labor')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
          />
        ),
      },
      {
        value: 'opEx',
        title: t('clarity.pnl.opEx'),
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_opex')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
          />
        ),
      },
      {
        value: 'rentAndOccupancy',
        title: t('clarity.pnl.rentAndOccupancy'),
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_rent_&_occupancy')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
          />
        ),
      },
      {
        value: 'aboveUnitAG',
        title: t('clarity.pnl.storeEbitda'),
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_above_unit')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
          />
        ),
      },
    ]

    return {
      tabs: configs.map((config) => createTab(config)),
    }
  }, [hierarchyData, pnlTableColumns, loading])

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing profit and loss page data...')
      pnlRefetch()
      storeHealthCheckRefetch()
      stackChartRefetch()
    }
  }, [refreshTrigger, pnlRefetch, storeHealthCheckRefetch, stackChartRefetch])

  // Show error toast if there is an error
  useEffect(() => {
    if (pnlMetricsError || storeHealthCheckError || stackChartError) {
      toast({
        title: t('common.errors.serverError'),
        description:
          pnlMetricsError?.message ||
          storeHealthCheckError?.message ||
          stackChartError?.message,
        variant: 'destructive',
      })
    }
  }, [pnlMetricsError, storeHealthCheckError, stackChartError])

  const getTruncatedBranchNames = (branchNames: string) => {
    const branches = branchNames.split(',').map((name) => name.trim())
    const fullText = branches.join(', ')

    if (branches.length <= 2) {
      return { displayText: fullText, fullText, shouldTruncate: false }
    }

    const firstBranch = branches[0]
    const secondBranch = branches[1]

    const displayText = `${firstBranch}, ${secondBranch}${branches.length > 2 ? ', ...' : ''}`

    return { displayText, fullText, shouldTruncate: true }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3 mb-10">
        <h2 className="text-2xl tracking-tight">
          {t('clarity.pnl.leaderboard')}
        </h2>
        <ScalrCarousel>
          <Leaderboard
            data={healthData?.netSales}
            title={t('clarity.pnl.netSales')}
            columns={storeHealthCheckColumns}
            loading={storeHealthCheckLoading}
            sortBy="percentageTrend"
            enablePagination
          />
          <Leaderboard
            data={healthData?.cogs}
            title={t('clarity.metrics.cogs')}
            columns={storeHealthCheckColumns}
            loading={storeHealthCheckLoading}
            sortBy="percentageTrend"
            sortOrder="asc"
            enablePagination
          />
          <Leaderboard
            data={healthData?.storeLabor}
            title={t('clarity.pnl.storeLabor')}
            columns={storeHealthCheckColumns}
            loading={storeHealthCheckLoading}
            sortBy="percentageTrend"
            sortOrder="asc"
            enablePagination
          />
          <Leaderboard
            data={healthData?.opEx}
            title={t('clarity.pnl.opEx')}
            columns={storeHealthCheckColumns}
            loading={storeHealthCheckLoading}
            sortBy="percentageTrend"
            sortOrder="asc"
            enablePagination
          />
          <Leaderboard
            data={healthData?.ebitda}
            title={t('clarity.pnl.ebitda')}
            columns={storeHealthCheckColumns}
            loading={storeHealthCheckLoading}
            sortBy="percentageTrend"
            sortOrder="asc"
            enablePagination
          />
        </ScalrCarousel>
      </div>

      <MasterStackBarChart
        title={t('clarity.pnl.breakdown')}
        metricCardRecord={metricCardRecord}
        miniCardData={metricCardData}
        trendData={metricCardTrendData}
        loading={stackChartLoading}
        miniMetricConfigs={MINI_METRIC_CONFIGS(t)}
        chartDataConfigs={CHART_DATA_CONFIGS(t)}
      />

      <div>
        <div className="flex items-center gap-2 mb-4">
          {(() => {
            const { displayText, fullText, shouldTruncate } =
              getTruncatedBranchNames(selectedBranch)

            if (!shouldTruncate) {
              return <h3 className="text-2xl tracking-tight">{displayText}</h3>
            }

            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <h3 className="text-2xl tracking-tight cursor-help">
                      {displayText}
                    </h3>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">{fullText}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          })()}
        </div>
        <Tabs defaultValue="sales" className="w-full">
          <TabsList className="w-full">
            {tabs.map((tab) => tab.trigger)}
          </TabsList>
          {tabs.map((tab) => tab.content)}
        </Tabs>
      </div>

      <DateRangeValidator
        dateRange={date}
        chartType="profit & loss chart"
        chartTitle={t('clarity.charts.profitAndLossChart')}
      >
        <MasterChart
          chartType="pnl"
          dateRange={date}
          selectedBranchFilter={selectedBranch}
          chartTitle={t('clarity.charts.profitAndLossChart')}
          isBeta
        />
      </DateRangeValidator>
    </div>
  )
}

function PnLTable({
  tableData,
  pnlTableColumns,
  loading,
}: {
  tableData: any[]
  pnlTableColumns: any[]
  loading: boolean
}) {
  return (
    <TreeTable
      data={tableData}
      columns={pnlTableColumns}
      isCard={false}
      loading={loading}
      fitContent={true}
    />
  )
}
